import { useEffect, useRef, useState, type ChangeEvent, type FunctionComponent } from 'react';
import classNames from 'classnames';
import { format } from 'date-fns';
import capitalize from 'lodash/capitalize';
import lowerCase from 'lodash/lowerCase';
import startCase from 'lodash/startCase';
import { useLocation } from 'react-router-dom';
import { ReferrerQueryString, track } from '@fff-web/fff-analytics';
import { getMembershipLabelMyAccount, IMembership } from '@fff-web/fff-utilities';
import { SUBSCRIPTION_STATUS } from '../../constants';
import { SubscriptionStatus } from '../MyAccountPage';
import RemainingBoxes from '../RemainingBoxes/RemainingBoxes';
import Button from '../shared-components/Button/Button';
import env from '../utils/env';
import { getProfileDates } from '../utils/getProfileDates';
import { MY_ACCOUNT_EXPERIENCE } from '../utils/segment';
import { getUserSubscriptionStatus } from '../utils/user';
import ProfileImage from './ProfileImage';
import * as styles from './Profile.module.css';

const CANCELED_REACTIVATE_URL = `https://${env.hostname}/reactivate-today`;
const EXPIRED_REACTIVATE_URL = `https://${env.hostname}/reactivate-my-membership`;
const UPGRADE_CONFIRMATION_URL = `https://${env.hostname}
/upgrade-confirmation?referrer=${ReferrerQueryString.myAccount}`;

const REACTIVATE_TRACKING_DATA = {
  name: 'Reactivation Started',
  ctaName: 'Reactivate Now',
  canceledMemberType: 'Canceled Member',
  expiredMemberType: 'Expired Member',
};

const UPGRADE_TRACKING_DATA = {
  name: 'Upgrade Started',
};

const handleClickCancelledReactivate = (subscriptionPlan: string) => {
  track({
    trackName: REACTIVATE_TRACKING_DATA.name,
    properties: {
      cta_name: REACTIVATE_TRACKING_DATA.ctaName,
      type: REACTIVATE_TRACKING_DATA.canceledMemberType,
      experience: MY_ACCOUNT_EXPERIENCE,
      reactivation_type: `Canceled ${capitalize(subscriptionPlan)}`,
    },
  }).finally(() => window.location.assign(CANCELED_REACTIVATE_URL));
};

const handleClickExpiredReactivate = () => {
  track({
    trackName: REACTIVATE_TRACKING_DATA.name,
    properties: {
      cta_name: REACTIVATE_TRACKING_DATA.ctaName,
      type: REACTIVATE_TRACKING_DATA.expiredMemberType,
      experience: MY_ACCOUNT_EXPERIENCE,
      reactivation_type: 'Expired',
    },
  }).finally(() => window.location.assign(EXPIRED_REACTIVATE_URL));
};

const handleClickUpgrade = (cta: string) => {
  track({
    trackName: UPGRADE_TRACKING_DATA.name,
    properties: {
      cta_name: cta,
      experience: MY_ACCOUNT_EXPERIENCE,
    },
  }).finally(() => window.location.assign(UPGRADE_CONFIRMATION_URL));
};

const SUBSCRIPTION_COPY = {
  seasonal: {
    text: 'Get the Most Out of Your Membership',
    CTA: 'UPGRADE TO ANNUAL',
    onClick: () => handleClickUpgrade('UPGRADE TO ANNUAL'),
  },
  trial: {
    text: `Let's Make it Official!`,
    CTA: 'ACTIVATE MY MEMBERSHIP',
    onClick: () => {
      window.location.href = `https://${env.hostname}/activate-membership`;
    },
  },
  CANCELED: {
    text: 'Come Back Today!',
    CTA: 'REACTIVATE NOW',
    onClick: handleClickCancelledReactivate,
  },
  CANCELLED_WITH_ZERO_BOXES: {
    text: 'Come Back Today!',
    CTA: 'REACTIVATE NOW',
    onClick: handleClickCancelledReactivate,
  },
  EXPIRED: {
    text: 'Come Back Today!',
    CTA: 'REACTIVATE NOW',
    onClick: handleClickExpiredReactivate,
  },
};

type DateSectionProps = {
  label: string;
  value: string;
  dataTestId?: string | null;
};

const DateSection: FunctionComponent<DateSectionProps> = ({ label, value, dataTestId = null }) => (
  <div style={value === 'N/A' ? { opacity: 0.6 } : {}} className={styles.activation}>
    <label htmlFor="date" data-testid={dataTestId}>
      {label}
      <span id="date">{value}</span>
    </label>
  </div>
);

export const MEMBER_SINCE_LABEL = 'Member Since: ';

const formatDate = (date: string | Date) => format(date, 'MMMM d, yyyy');

type ProfileProps = {
  isDesktop: boolean;
  firstName: string;
  lastName: string;
  activationDate: string | Date;
  subscriptionPlan: string;
  subscriptionState: SubscriptionStatus;
  avatarUrl: string | null;
  uploadAvatar: (formData: FormData) => void;
  remainingBoxes: {
    orderState: string;
    season: string;
  }[];
  displayRemainingBoxes: boolean;
  nextRenewal: {
    year: number;
    season: string;
    renewalDate: string | Date;
  };
  membershipInfo: IMembership;
  isClubMember: boolean;
};

const Profile: FunctionComponent<ProfileProps> = ({
  isDesktop,
  firstName,
  lastName,
  activationDate,
  subscriptionPlan,
  subscriptionState,
  avatarUrl,
  uploadAvatar,
  remainingBoxes,
  displayRemainingBoxes,
  nextRenewal,
  membershipInfo,
  isClubMember,
}) => {
  const inputFile = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const [mobileOnMyAccountPage, setMobileOnMyAccountPage] = useState(false);
  const { year, season, renewalDate } = nextRenewal;
  const { SUBSCRIPTION_STATE, COLOR } = getUserSubscriptionStatus(subscriptionState);
  const subscriptionCopy =
    SUBSCRIPTION_COPY[subscriptionState as keyof typeof SUBSCRIPTION_COPY] ||
    SUBSCRIPTION_COPY[subscriptionPlan as keyof typeof SUBSCRIPTION_COPY];

  const isActiveBoxSubscription =
    subscriptionState === SUBSCRIPTION_STATUS.ACTIVE && !membershipInfo?.isRefillSubscription;

  const nextRenewalDate = renewalDate ? formatDate(renewalDate) : 'N/A';
  const nextRenewalSeason = season && year ? `${startCase(lowerCase(season))} ${year}` : 'N/A';

  const openFileDialog = () => {
    inputFile.current?.click();
  };

  const fileChangedHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const formData = new FormData();
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    formData.append('file', selectedFile);
    uploadAvatar(formData);
  };

  useEffect(() => {
    const isMobileOnMyAccountPage =
      !isDesktop &&
      !(
        location.pathname.includes('orders') ||
        location.pathname.includes('invoice-history') ||
        location.pathname.includes('personal-survey') ||
        location.pathname.includes('rate-and-review')
      );
    setMobileOnMyAccountPage(isMobileOnMyAccountPage);
  }, [location, isDesktop]);

  const profileDates = getProfileDates(membershipInfo);

  return (
    <div className={styles.topContainer}>
      <div className={styles.desktopContainer}>
        <input
          ref={inputFile}
          type="file"
          accept="image/jpeg, image/png"
          className={styles.fileDialog}
          onChange={fileChangedHandler}
        />
        {isDesktop && <ProfileImage avatarUrl={avatarUrl} openFileDialog={openFileDialog} />}
        <div className={styles.container} data-testid="profile-container">
          <div className={styles.name} data-testid="name-text">{`Hello, ${firstName}!`}</div>
          <>
            <div className={styles.statusContainer}>
              <div className={styles.subscription} data-testid="subscription-type">
                {getMembershipLabelMyAccount({ membershipInfo, subscriptionState })}
              </div>
              {subscriptionState && (
                <div
                  className={classNames(styles.subscriptionState, styles[COLOR])}
                  data-testid="subscription-state"
                >
                  {SUBSCRIPTION_STATE}
                </div>
              )}
            </div>
            <div className={styles.activationContainer}>
              {isActiveBoxSubscription ? (
                <>
                  {renewalDate || isClubMember ? (
                    <DateSection label="Next Billing Date: " value={nextRenewalDate} />
                  ) : (
                    <DateSection
                      label="Next Billing Season: "
                      value={nextRenewalSeason}
                      dataTestId="next-billing-season-text"
                    />
                  )}
                  {activationDate && (
                    <DateSection
                      label={`${MEMBER_SINCE_LABEL} `}
                      value={formatDate(activationDate)}
                      dataTestId="activation-date-text"
                    />
                  )}
                  {!isClubMember && (
                    <a
                      href="https://support.fabfitfun.com/en/articles/4-billing-shipping-schedule"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="billing-shipping-link"
                    >
                      Billing / Shipping Schedule
                    </a>
                  )}
                </>
              ) : (
                <>
                  {profileDates.length ? (
                    profileDates.map((dateInfo, index) =>
                      dateInfo.date || activationDate ? (
                        <DateSection
                          key={index}
                          label={dateInfo.label}
                          value={dateInfo.date || formatDate(activationDate)}
                        />
                      ) : null,
                    )
                  ) : (
                    <>
                      {activationDate && (
                        <DateSection
                          label={`${MEMBER_SINCE_LABEL} `}
                          value={formatDate(activationDate)}
                          dataTestId="activation-date-text"
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </>
        </div>
        {displayRemainingBoxes && <RemainingBoxes remainingBoxes={remainingBoxes} />}
        {!isClubMember && subscriptionCopy && isDesktop && (
          <div className={styles.desktopReactivate}>
            <div className={styles.left} />
            <div className={styles.reactivate}>
              <h3>{subscriptionCopy.text}</h3>
              <Button onClick={() => subscriptionCopy.onClick(subscriptionPlan)}>
                {subscriptionCopy.CTA}
              </Button>
            </div>
            <div className={styles.right} />
          </div>
        )}
      </div>
      {!isClubMember && subscriptionCopy && !isDesktop && (
        <div className={styles.reactivate}>
          <h3>{subscriptionCopy.text}</h3>
          <Button onClick={() => subscriptionCopy.onClick(subscriptionPlan)}>
            {subscriptionCopy.CTA}
          </Button>
        </div>
      )}
      {mobileOnMyAccountPage && (
        <div className={styles.imageSection}>
          <div className={styles.header}>Account Details</div>
          <ProfileImage avatarUrl={avatarUrl} openFileDialog={openFileDialog} />
          <div className={styles.fullname}>{`${firstName} ${lastName}`}</div>
          <div>
            <button type="button" onClick={openFileDialog}>
              Update Photo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
